/**
 * Разметка в поле ввода: панель форматирования над выделением и Ctrl/⌘+B, I, U.
 *
 * Здесь только чистые функции над текстом и выделением, без DOM: что заменить, чем и что
 * выделить после. Синтаксис — как у Discord, его же понимает renderMarkdown: **жирный**,
 * *курсив*, __подчёркнутый__, ~~зачёркнутый~~, ||спойлер||, `код` и ```блок```,
 * «> » — цитата, «# », «## », «### » — заголовки.
 *
 * Повторное нажатие снимает то же оформление. Выделение после правки остаётся на самом тексте,
 * без знаков разметки: следующее нажатие ляжет на тот же текст и вложится правильно
 * (жирный, потом курсив — ***текст***).
 *
 * Ссылка — [текст](адрес): адрес спрашивает панель, поэтому для неё отдельные функции внизу.
 */

export type InlineFormat = 'bold' | 'italic' | 'underline' | 'strike' | 'spoiler' | 'code'
export type LineFormat = 'h1' | 'h2' | 'h3' | 'text' | 'quote'
export type Format = InlineFormat | LineFormat

/** Правка поля: заменить [from, to) на insert, затем выделить [start, end) в новом тексте. */
export interface Edit {
  from: number
  to: number
  insert: string
  start: number
  end: number
}

const MARK: Record<InlineFormat, { ch: string; len: number }> = {
  bold: { ch: '*', len: 2 },
  italic: { ch: '*', len: 1 },
  underline: { ch: '_', len: 2 },
  strike: { ch: '~', len: 2 },
  spoiler: { ch: '|', len: 2 },
  code: { ch: '`', len: 1 },
}

const LINE_FORMATS = new Set<Format>(['h1', 'h2', 'h3', 'text', 'quote'])

/**
 * Префиксы строки, которые оформление не трогает: цитата и заголовок или подпись после неё.
 * Пробел после знака — ровно один: остальные принадлежат тексту и после снятия возвращаются.
 */
const QUOTE_PREFIX = /^ *>(?:>>)? /
const SIZE_PREFIX = /^(?:#{1,3}|-#) /

export function applyFormat(text: string, start: number, end: number, format: Format): Edit {
  const s = Math.max(0, Math.min(start, end, text.length))
  const e = Math.min(text.length, Math.max(start, end))
  return LINE_FORMATS.has(format) ? toggleLines(text, s, e, format as LineFormat) : toggleInline(text, s, e, format as InlineFormat)
}

/** Оформление уже есть и нажатие его снимет — кнопка на панели горит. */
export function isFormatActive(text: string, start: number, end: number, format: Format): boolean {
  if (format === 'text') return false
  const edit = applyFormat(text, start, end, format)
  return edit.insert.length < edit.to - edit.from
}

/** Сколько символов ch подряд стоит перед позицией i. */
function runBefore(text: string, i: number, ch: string): number {
  let n = 0
  while (i - n - 1 >= 0 && text[i - n - 1] === ch) n++
  return n
}

/** Сколько символов ch подряд стоит начиная с позиции i. */
function runAfter(text: string, i: number, ch: string): number {
  let n = 0
  while (i + n < text.length && text[i + n] === ch) n++
  return n
}

/**
 * Есть ли оформление при n знаках с обеих сторон. Звёздочки общие у жирного и курсива:
 * две — жирный, одна — курсив, три — оба сразу.
 */
function present(format: InlineFormat, n: number): boolean {
  if (format === 'italic') return n % 2 === 1
  if (format === 'code') return n === 1
  return n >= MARK[format].len
}

/** Текст целиком обёрнут в знаки формата, и внутри их больше нет: «**x**», но не «**a** и **b**». */
function wrapped(chunk: string, format: InlineFormat): boolean {
  const { ch, len } = MARK[format]
  const lead = runAfter(chunk, 0, ch)
  const trail = runBefore(chunk, chunk.length, ch)
  if (lead + trail >= chunk.length) return false
  const core = chunk.slice(lead, chunk.length - trail)
  return present(format, Math.min(lead, trail)) && !core.includes(ch.repeat(len))
}

/**
 * Что из выделения оборачивать: без пробелов по краям (Discord не считает «** x **» жирным)
 * и без «> » или «# » в начале строки — иначе заголовок и цитата перестанут ими быть.
 */
function content(text: string, s: number, e: number): [number, number] {
  const lineStart = s === 0 ? 0 : text.lastIndexOf('\n', s - 1) + 1
  const nl = text.indexOf('\n', lineStart)
  const line = text.slice(lineStart, nl < 0 ? text.length : nl)
  const quote = QUOTE_PREFIX.exec(line)?.[0] ?? ''
  const prefix = lineStart + quote.length + (SIZE_PREFIX.exec(line.slice(quote.length))?.[0].length ?? 0)
  // выделение начинается в «> » или «# » — начинаем за ними; выделены одни они — пара встанет за ними
  let a = Math.max(s, prefix)
  let b = Math.max(e, a)
  while (a < b && /\s/.test(text[a] ?? '')) a++
  while (b > a && /\s/.test(text[b - 1] ?? '')) b--
  return [a, b]
}

function toggleInline(text: string, s: number, e: number, format: InlineFormat): Edit {
  const { ch, len } = MARK[format]
  const mark = ch.repeat(len)
  const multiline = text.slice(s, e).includes('\n')
  // блок кода — и когда выделено несколько строк, и когда выделение уже стоит внутри блока
  const fenced = /```[^\s`]*\n$/.test(text.slice(0, s)) && /^\n```/.test(text.slice(e))
  if (format === 'code' && (multiline || fenced)) return toggleFence(text, s, e)

  const [a, b] = content(text, s, e)

  // знаки снаружи выделения: **|текст|** — снимаем
  if (present(format, Math.min(runBefore(text, a, ch), runAfter(text, b, ch)))) {
    return { from: a - len, to: b + len, insert: text.slice(a, b), start: a - len, end: b - len }
  }
  // выделено вместе со знаками: |**текст**| — тоже снимаем
  const chunk = text.slice(a, b)
  if (wrapped(chunk, format)) {
    const inner = chunk.slice(len, chunk.length - len)
    return { from: a, to: b, insert: inner, start: a, end: a + inner.length }
  }
  if (multiline) return toggleEachLine(text, s, e, format)
  return { from: a, to: b, insert: mark + chunk + mark, start: a + len, end: b + len }
}

/**
 * Несколько строк оформляются построчно: курсив и код через перевод строки не тянутся,
 * а цитата или заголовок на строке рвут любую разметку, начатую выше.
 * Выделение после правки — вместе со знаками: так повторное нажатие найдёт их на каждой строке.
 */
function toggleEachLine(text: string, s: number, e: number, format: InlineFormat): Edit {
  const { ch, len } = MARK[format]
  const mark = ch.repeat(len)
  const parts: [number, number][] = []
  for (let i = s; ; ) {
    const nl = text.indexOf('\n', i)
    const lineEnd = nl < 0 || nl > e ? e : nl
    const [a, b] = content(text, i, lineEnd)
    if (text.slice(a, b).trim()) parts.push([a, b])
    if (nl < 0 || nl >= e) break
    i = nl + 1
  }
  const off = parts.length > 0 && parts.every(([a, b]) => wrapped(text.slice(a, b), format))
  let insert = ''
  let at = s
  for (const [a, b] of parts) {
    const chunk = text.slice(a, b)
    insert += text.slice(at, a) + (off ? chunk.slice(len, chunk.length - len) : wrapped(chunk, format) ? chunk : mark + chunk + mark)
    at = b
  }
  insert += text.slice(at, e)
  return { from: s, to: e, insert, start: s, end: s + insert.length }
}

/** Код на несколько строк — блок: выделенные строки встают между оградами ```. */
function toggleFence(text: string, s: number, e: number): Edit {
  // уже в блоке: ```\n|…|\n``` или выделен весь блок вместе с оградами — снимаем
  const open = /```[^\s`]*\n$/.exec(text.slice(0, s))
  const close = /^\n```/.exec(text.slice(e))
  if (open && close) {
    const from = s - open[0].length
    return { from, to: e + close[0].length, insert: text.slice(s, e), start: from, end: from + (e - s) }
  }
  const inner = /^```[^\s`]*\n([\s\S]*)\n```$/.exec(text.slice(s, e))?.[1]
  if (inner !== undefined) return { from: s, to: e, insert: inner, start: s, end: s + inner.length }
  // перевод строки по краю выделения (тройной клик) остаётся снаружи блока
  let a = s
  let b = e
  if (a < b && text[a] === '\n') a++
  if (b > a && text[b - 1] === '\n') b--
  const chunk = text.slice(a, b)
  const insert = '```\n' + chunk + '\n```'
  return { from: a, to: b, insert, start: a + 4, end: a + 4 + chunk.length }
}

/**
 * Заголовки, «обычный текст» и цитата меняют начало каждой строки, которой касается выделение.
 * Заголовок заменяет прежний уровень, «обычный текст» убирает его; цитата ставится перед
 * заголовком («> # текст»), как её понимает Discord.
 */
function toggleLines(text: string, s: number, e: number, format: LineFormat): Edit {
  // выделение, кончающееся в самом начале строки (тройной клик), эту строку не захватывает
  const last = e > s && text[e - 1] === '\n' ? e - 1 : e
  // lastIndexOf с -1 ищет с нуля и нашёл бы перевод строки в самом начале текста
  const from = s === 0 ? 0 : text.lastIndexOf('\n', s - 1) + 1
  const nl = text.indexOf('\n', last)
  const to = nl < 0 ? text.length : nl
  const lines = text.slice(from, to).split('\n').map((line) => {
    const quote = QUOTE_PREFIX.exec(line)?.[0] ?? ''
    const size = SIZE_PREFIX.exec(line.slice(quote.length))?.[0] ?? ''
    return { line, quote, size, rest: line.slice(quote.length + size.length) }
  })

  // head — новое начало строки вместо её первых old символов
  let next: { head: string; old: number }[]
  if (format === 'quote') {
    const off = lines.every((l) => l.quote)
    // пустые строки тоже цитируем: иначе цитата рвётся на абзацы
    next = lines.map((l) => ({ head: off ? '' : l.quote || '> ', old: l.quote.length }))
  } else {
    const level = format === 'h1' ? '# ' : format === 'h2' ? '## ' : format === 'h3' ? '### ' : ''
    const filled = lines.filter((l) => l.rest.trim())
    const off = level !== '' && filled.length > 0 && filled.every((l) => l.size.trimEnd() === level.trimEnd())
    // пустую строку не трогаем: «# » без текста — не заголовок
    next = lines.map((l) => ({ head: l.quote + (!l.rest.trim() ? l.size : off ? '' : level), old: l.quote.length + l.size.length }))
  }

  const out: string[] = []
  let oldAt = from
  let newAt = from
  let start = s
  let end = e
  lines.forEach((l, i) => {
    const n = next[i]!
    const body = l.line.slice(n.old)
    // позиция в старой строке → в новой; внутри заменённого начала — сразу за новым началом
    const map = (p: number) => (p - oldAt <= n.old ? newAt + n.head.length : newAt + n.head.length + (p - oldAt - n.old))
    if (s >= oldAt && s <= oldAt + l.line.length) start = map(s)
    if (e >= oldAt && e <= oldAt + l.line.length) end = map(e)
    out.push(n.head + body)
    oldAt += l.line.length + 1
    newAt += n.head.length + body.length + 1
  })
  const insert = out.join('\n')
  if (e > to) end = e - (to - from) + insert.length
  return { from, to, insert, start, end: Math.max(start, end) }
}

/**
 * Ссылка с текстом, как её разбирает renderMarkdown: текст в одну строку без квадратных скобок,
 * адрес — http или https; скобки в адресе только парные, иначе адрес в <…>.
 */
const LINK = /\[([^[\]\n]+)\]\((?:<(https?:\/\/[^\s<>]+)>|(https?:\/\/(?:[^\s()<>]|\([^\s()<>]*\))+))\)/g
const BARE_URL = /^https?:\/\/(?:[^\s()<>]|\([^\s()<>]*\))+$/

/** Ссылка вокруг выделения: [from, to) — вся разметка, [labelStart, labelEnd) — её текст. */
export interface LinkSpan {
  from: number
  to: number
  labelStart: number
  labelEnd: number
}

/** Выделение внутри ссылки или это она целиком — кнопка горит, нажатие снимет ссылку. */
export function linkAt(text: string, start: number, end: number): LinkSpan | null {
  const [s, e] = content(text, Math.min(start, end), Math.max(start, end))
  const lineStart = s === 0 ? 0 : text.lastIndexOf('\n', s - 1) + 1
  const nl = text.indexOf('\n', s)
  const lineEnd = nl < 0 ? text.length : nl
  if (e > lineEnd) return null
  for (const m of text.slice(lineStart, lineEnd).matchAll(LINK)) {
    const from = lineStart + m.index!
    const to = from + m[0].length
    if (s >= from && e <= to) return { from, to, labelStart: from + 1, labelEnd: from + 1 + m[1]!.length }
  }
  return null
}

/** Снять ссылку: остаётся её текст, он и выделен. */
export function unlink(text: string, link: LinkSpan): Edit {
  const label = text.slice(link.labelStart, link.labelEnd)
  return { from: link.from, to: link.to, insert: label, start: link.from, end: link.from + label.length }
}

/**
 * Почему выделение не станет текстом ссылки: оно на несколько строк, в нём квадратные скобки
 * или одни пробелы. null — станет.
 */
export function linkBlocker(text: string, start: number, end: number): 'lines' | 'brackets' | 'empty' | null {
  const [a, b] = content(text, Math.min(start, end), Math.max(start, end))
  const label = text.slice(a, b)
  if (label.includes('\n')) return 'lines'
  if (/[[\]]/.test(label)) return 'brackets'
  return label ? null : 'empty'
}

/**
 * Адрес для ссылки из набранного или вставленного. Без схемы — https: «klichat.ru» — это
 * https://klichat.ru. Другие схемы (mailto:, javascript:) и то, что не похоже на адрес сайта, — null.
 */
export function linkTarget(raw: string): string | null {
  let url = raw.trim().replace(/^<(.*)>$/, '$1')
  if (/^https?:\/\//i.test(url)) url = url.replace(/^https?/i, (m) => m.toLowerCase())
  // схема, но не порт: «mailto:», а не «klichat.ru:8080»
  else if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(url)) return null
  else url = `https://${url.replace(/^\/+/, '')}`
  if (/\s/.test(url)) return null
  url = url.replace(/</g, '%3C').replace(/>/g, '%3E')
  let host: string
  try {
    host = new URL(url).hostname
  } catch {
    return null
  }
  // у сайта есть зона: «klichat.ru», а не «привет»
  return /\.[^.]+$/.test(host) ? url : null
}

/** Выделение становится текстом ссылки на url (из linkTarget). Курсор — сразу за ссылкой. */
export function linkEdit(text: string, start: number, end: number, url: string): Edit | null {
  if (linkBlocker(text, start, end) || linkAt(text, start, end)) return null
  const [a, b] = content(text, Math.min(start, end), Math.max(start, end))
  const insert = `[${text.slice(a, b)}](${BARE_URL.test(url) ? url : `<${url}>`})`
  return { from: a, to: b, insert, start: a + insert.length, end: a + insert.length }
}

/** Выделенное целиком — адрес или домен: «https://…», «klichat.ru/stats». */
const ADDRESS = /^(?:[a-z][a-z0-9+.-]*:\/\/\S*|(?:[\p{L}\p{N}-]+\.)+\p{L}{2,}(?:[/?#:]\S*)?)$/iu

/**
 * Адрес, вставленный поверх выделенного текста, делает из текста ссылку, как в Discord.
 * Выделен сам адрес или вставляют не адрес — null: вставка обычная.
 */
export function pasteLink(text: string, start: number, end: number, pasted: string): Edit | null {
  const url = pasted.trim()
  const selected = text.slice(Math.min(start, end), Math.max(start, end)).trim()
  if (!selected || !/^https?:\/\/\S+$/i.test(url) || ADDRESS.test(selected)) return null
  const target = linkTarget(url)
  return target ? linkEdit(text, start, end, target) : null
}
